/**
 * Sidebar structure (SPEC-FRONTEND §7): labels come from the `nav` namespace, badge counters
 * from `GET /api/admin/queues/`.
 */
import type { MantineColor } from '@mantine/core';
import {
  IconArrowsExchange,
  IconFlag,
  IconHistory,
  IconId,
  IconLayoutDashboard,
  IconMail,
  IconMessageReport,
  IconPackage,
  IconSettings,
  IconUsers,
  IconUserX,
  type TablerIcon,
} from '@tabler/icons-react';

import type { AdminQueues } from '@/api/types';
import { paths } from '@/utils/paths';

export type NavItemKey =
  | 'dashboard'
  | 'verifications'
  | 'complaints'
  | 'suspicious'
  | 'blocking'
  | 'users'
  | 'shipments'
  | 'deals'
  | 'settings'
  | 'outbox'
  | 'audit';

export type NavSectionKey = 'queues' | 'records' | 'system';

/** Keys of secondary label lines under `nav:items.*`. */
export type NavHintKey = 'verificationsHint';

export type QueueBadgeKey = 'verifications' | 'complaints' | 'suspicious' | 'candidates' | 'blocked';

export interface QueueBadge {
  key: QueueBadgeKey;
  /** Counter field of `AdminQueues`. */
  counter: keyof AdminQueues;
  color: MantineColor;
  /** Actionable queues are filled; informational counters are light. */
  variant: 'filled' | 'light';
}

export interface NavItem {
  key: NavItemKey;
  to: string;
  icon: TablerIcon;
  /** Active only on the exact path (the dashboard). */
  exact?: boolean;
  /** More specific sibling routes that must not highlight this item. */
  exclude?: readonly string[];
  /** Secondary line under the label (`nav:items.<hint>`). */
  hint?: NavHintKey;
  badges?: readonly QueueBadge[];
}

export interface NavSection {
  /** `null` for the top block without a heading. */
  key: NavSectionKey | null;
  items: readonly NavItem[];
}

export const NAV_SECTIONS: readonly NavSection[] = [
  {
    key: null,
    items: [{ key: 'dashboard', to: paths.dashboard, icon: IconLayoutDashboard, exact: true }],
  },
  {
    key: 'queues',
    items: [
      {
        key: 'verifications',
        to: paths.verifications,
        icon: IconId,
        hint: 'verificationsHint',
        badges: [{ key: 'verifications', counter: 'verifications_pending', color: 'brand', variant: 'filled' }],
      },
      {
        key: 'complaints',
        to: paths.complaints,
        icon: IconMessageReport,
        badges: [{ key: 'complaints', counter: 'complaints_open', color: 'red', variant: 'filled' }],
      },
      {
        key: 'suspicious',
        to: paths.shipmentsSuspicious,
        icon: IconFlag,
        badges: [{ key: 'suspicious', counter: 'shipments_flagged', color: 'orange', variant: 'filled' }],
      },
      {
        key: 'blocking',
        to: paths.usersBlocking,
        icon: IconUserX,
        badges: [
          { key: 'candidates', counter: 'users_to_review', color: 'orange', variant: 'filled' },
          { key: 'blocked', counter: 'users_blocked', color: 'gray', variant: 'light' },
        ],
      },
    ],
  },
  {
    key: 'records',
    items: [
      { key: 'users', to: paths.users, icon: IconUsers, exclude: [paths.usersBlocking] },
      { key: 'shipments', to: paths.shipments, icon: IconPackage, exclude: [paths.shipmentsSuspicious] },
      { key: 'deals', to: paths.deals, icon: IconArrowsExchange },
    ],
  },
  {
    key: 'system',
    items: [
      { key: 'settings', to: paths.settings, icon: IconSettings },
      { key: 'outbox', to: paths.outbox, icon: IconMail },
      { key: 'audit', to: paths.audit, icon: IconHistory },
    ],
  },
];

function isWithin(pathname: string, base: string): boolean {
  return pathname === base || pathname.startsWith(`${base}/`);
}

export function isNavItemActive(pathname: string, item: NavItem): boolean {
  if (item.exact) return pathname === item.to;
  return isWithin(pathname, item.to) && !(item.exclude ?? []).some((path) => isWithin(pathname, path));
}

/** Compact counter for a badge: 1…99, then «99+». */
export function formatBadgeCount(count: number): string {
  return count > 99 ? '99+' : String(count);
}
